import React, { useRef, useState } from 'react';
import { useAuth } from '@/auth/clerkShim';
import { Upload, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import useAdminStore from '@store/adminStore';
import {
  SPORT_NAMES,
  getColorPaletteList,
  getBannerStyleList,
  getDefaultStyleKey,
} from '@staty/shared/banners';
import useGenerationJob from '../generation/useGenerationJob';
import VariantGrid from '../generation/VariantGrid';

/**
 * Sport-aware placeholder hints for the Extra-direction input (owner
 * 2026-09-25): the two most common logo motifs per sport, shown as
 * "e.g. <a>, <b>". Keys mirror SPORT_COLOR_PALETTES; unknown sports get the
 * generic pair. Placeholder only — never sent to the generator.
 */
/**
 * Vibe tiles (owner 2026-09-25): same treatment as the Style tiles — the
 * vibe name mocked up in that vibe's feel. CSS-only, system-safe fonts.
 */
const VIBE_PREVIEWS: Record<'pro' | 'amateur' | 'social', {
  name: string; caption: string; container: React.CSSProperties; text: React.CSSProperties;
}> = {
  pro: {
    name: 'PRO',
    caption: 'Polished franchise look',
    container: { background: 'linear-gradient(160deg, #111827 0%, #1e3a5f 100%)' },
    text: {
      fontFamily: "'Arial Narrow', 'Helvetica Neue', sans-serif",
      fontWeight: 800, fontStyle: 'italic', fontSize: 18, letterSpacing: '0.06em', color: '#e5e7eb',
      textShadow: '0 1px 0 #6b7280, 0 2px 6px rgba(0,0,0,0.6)',
    },
  },
  amateur: {
    name: 'Amateur',
    caption: 'Friendly rec-league feel',
    container: { background: '#f0fdf4' },
    text: {
      fontFamily: "'Chalkboard SE', 'Comic Sans MS', 'Segoe UI', sans-serif",
      fontWeight: 700, fontSize: 15, color: '#15803d',
    },
  },
  social: {
    name: 'Social!',
    caption: 'Playful, just for fun',
    container: { background: 'linear-gradient(135deg, #fff1f2 0%, #ffe4e6 60%, #fef3c7 100%)' },
    text: {
      fontFamily: "'Marker Felt', 'Snell Roundhand', 'Segoe Print', cursive",
      fontWeight: 700, fontSize: 17, color: '#e11d48', transform: 'rotate(-3deg)',
      textShadow: '1px 1px 0 #ffffff',
    },
  },
};

const LOGO_MOTIF_HINTS: Record<string, string> = {
  basketball: 'basketball silhouette, winged hoop',
  soccer: 'soccer ball, crest shield',
  volleyball: 'volleyball silhouette, beach wave',
  pickleball: 'crossed paddles, ball with motion lines',
  softball: 'crossed bats, stitched ball',
  baseball: 'crossed bats, diamond outline',
  golf: 'flag and pin, swinging silhouette',
  tennis: 'racquet silhouette, tennis ball seam',
  track: 'winged shoe, finish-line ribbon',
  judo: 'belted gi silhouette, rising-sun circle',
  default: 'mascot silhouette, shield crest',
};

/**
 * CSS-only mock-logo previews for the Style tiles (owner 2026-09-25): the
 * style's NAME stands in for the org name, dressed the way that style's
 * generated logo would feel. Pure suggestion — the real output comes from
 * the generator; these just make the choice visual. Font stacks are
 * system-safe (no webfont loads).
 */
const STYLE_PREVIEWS: Record<string, { container: React.CSSProperties; text: React.CSSProperties }> = {
  minimal: {
    container: { background: '#ffffff' },
    text: {
      fontFamily: "'Helvetica Neue', Inter, Arial, sans-serif",
      fontWeight: 300, fontSize: 17, letterSpacing: '0.3em', textTransform: 'uppercase', color: '#111827',
    },
  },
  photo_realistic: {
    container: {
      background: 'radial-gradient(120% 160% at 20% 0%, #6b7280 0%, #1f2937 45%, #030712 100%)',
    },
    text: {
      fontFamily: "Georgia, 'Times New Roman', serif",
      fontWeight: 600, fontSize: 16, letterSpacing: '0.06em', color: '#f9fafb',
      textShadow: '0 2px 10px rgba(0,0,0,0.8), 0 0 24px rgba(255,255,255,0.25)',
    },
  },
  illustrated: {
    container: { background: 'linear-gradient(135deg, #fdf6e3 0%, #f7e8c8 100%)' },
    text: {
      fontFamily: "'Snell Roundhand', 'Savoye LET', 'Segoe Script', 'Brush Script MT', cursive",
      fontWeight: 700, fontSize: 21, color: '#92400e', transform: 'rotate(-4deg)',
      textShadow: '1px 1px 0 rgba(146,64,14,0.2)',
    },
  },
  graphic_novel: {
    container: {
      background: 'radial-gradient(circle at 1px 1px, rgba(17,24,39,0.28) 1px, transparent 1.4px) 0 0 / 7px 7px, #facc15',
    },
    text: {
      fontFamily: "'Arial Black', 'Impact', sans-serif",
      fontWeight: 900, fontSize: 15, fontStyle: 'italic', textTransform: 'uppercase',
      letterSpacing: '0.02em', color: '#111827', textShadow: '2.5px 2.5px 0 #ffffff, 4px 4px 0 #ef4444',
    },
  },
  retro: {
    container: {
      background: 'linear-gradient(#fef3c7 0 72%, #b45309 72% 79%, #ea580c 79% 86%, #f59e0b 86% 93%, #fbbf24 93% 100%)',
    },
    text: {
      fontFamily: "'Cooper Black', 'Chalkboard SE', Georgia, serif",
      fontWeight: 800, fontSize: 17, letterSpacing: '0.08em', color: '#7c2d12',
      textShadow: '1.5px 1.5px 0 #fde68a',
    },
  },
  neon: {
    container: { background: '#0a0a14' },
    text: {
      fontFamily: "'Avenir Next', Futura, Inter, sans-serif",
      fontWeight: 600, fontSize: 16, letterSpacing: '0.14em', textTransform: 'uppercase', color: '#67e8f9',
      textShadow: '0 0 6px #22d3ee, 0 0 16px #22d3ee, 0 0 30px #a21caf',
    },
  },
};

type AnyDoc = Record<string, any>;

interface LogoEditorProps {
  league: AnyDoc | null;
  admin: AnyDoc | null;
  /** Narrow hosting column (Appearance desktop-preview mode) → 1-col grids. */
  narrow?: boolean;
  /** Fires with the UPDATED org doc after upload or variant save — the
   * caller refreshes its org so hasLogo/logoExt aren't stale. */
  onSaved?: (savedOrg: AnyDoc | null) => void;
  /** Reports the picked (unsaved) AI variant for the page-level preview. */
  onDraftChange?: (draft: { imageUrl: string } | null) => void;
  /** Bumped when the HOSTING PAGE saved the pending variant itself (the
   * Appearance page's Save commits logo drafts) — clears the selection. */
  externalSaveSignal?: number;
}

const CDN = 'https://staty-bucket.sfo2.cdn.digitaloceanspaces.com';

/**
 * Create/Upload Logo (2026-09-09) — the thin logo counterpart of
 * BannerEditor: upload + AI generate only (no template mode; a logo has no
 * text and no layout). ONE logo serves every template on light AND dark
 * grounds, so generation prompts for a transparent square emblem. Shares
 * useGenerationJob + VariantGrid with BannerEditor.
 */
export default function LogoEditor({ league, admin, narrow = false, onSaved, onDraftChange, externalSaveSignal = 0 }: LogoEditorProps) {
  const { getToken } = useAuth();
  const fileRef = useRef<HTMLInputElement>(null);

  // Generate-first (owner 2026-09-25): AI generation is the default tab —
  // most orgs arrive without a logo file, and the generator is the showcase.
  const [mode, setMode] = useState<'upload' | 'generate'>('generate');
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [selectedVariantUrl, setSelectedVariantUrl] = useState<string | null>(null);

  const [selectedVibe, setSelectedVibe] = useState<'pro' | 'amateur' | 'social'>('pro');
  const [selectedStyle, setSelectedStyle] = useState<string>(getDefaultStyleKey());
  const [paletteKey, setPaletteKey] = useState<string>('');
  const [useCustomColors, setUseCustomColors] = useState(false);
  const [customPrimary, setCustomPrimary] = useState('#2563eb');
  const [customSecondary, setCustomSecondary] = useState('#f59e0b');
  const [promptAddendum, setPromptAddendum] = useState('');

  const postLeagueLogo = useAdminStore((state: any) => state.postLeagueLogo);
  const generateLogo = useAdminStore((state: any) => state.generateLogo);
  const getLogoJobStatus = useAdminStore((state: any) => state.getLogoJobStatus);
  const selectLogoVariant = useAdminStore((state: any) => state.selectLogoVariant);

  const leagueSport = Array.isArray(league?.sports) && league?.sports.length > 0 ? league.sports[0] : 'basketball';
  const paletteList = getColorPaletteList(leagueSport);
  const activePalette = (paletteKey && paletteList.find((p) => p.key === paletteKey)) || paletteList[0];

  const generation = useGenerationJob({
    startJob: async (input) => {
      const token = await getToken();
      return generateLogo(league?._id, input, token);
    },
    getJobStatus: getLogoJobStatus,
    getToken,
    offlineMessage: 'Generation is not starting. The logo worker appears to be offline. Please try again later.',
  });

  const isValidHex = (v: string) => /^#[0-9a-f]{6}$/i.test(v);

  // Hosting page committed the pending variant (page-level Save) — drop the
  // local selection so the grid doesn't claim an unsaved pick.
  React.useEffect(() => {
    if (externalSaveSignal > 0) setSelectedVariantUrl(null);
  }, [externalSaveSignal]);

  const reportDraft = (url: string | null) => {
    setSelectedVariantUrl(url);
    onDraftChange?.(url ? { imageUrl: url } : null);
  };

  const uploadFile = async (file: File) => {
    if (!league?.name || !admin?._id) return;
    setUploading(true);
    const token = await getToken();
    postLeagueLogo(file, league.name, admin._id, token, (result: AnyDoc | null) => {
      setUploading(false);
      if (result) onSaved?.(result);
    });
  };

  const startGeneration = (basedOnUrl: string | null, overrides?: { addendumExtra?: string; styleOverride?: string }) => {
    if (!league?._id) {
      generation.fail('Your organization is still loading. Try again in a moment.', 'idle');
      return;
    }
    if (useCustomColors && (!isValidHex(customPrimary) || !isValidHex(customSecondary))) {
      generation.fail('Custom colors must be valid 6-digit hex values (e.g. #2563eb).');
      return;
    }
    const palette = useCustomColors
      ? { primary: customPrimary, secondary: customSecondary }
      : { primary: activePalette.primary, secondary: activePalette.secondary };
    const combinedAddendum = [promptAddendum.trim(), overrides?.addendumExtra?.trim()]
      .filter(Boolean)
      .join('. ');
    const input: AnyDoc = {
      orgName: league.displayName || league.name,
      sport: leagueSport,
      vibe: selectedVibe,
      style: overrides?.styleOverride || selectedStyle,
      palette,
    };
    if (combinedAddendum) input.promptAddendum = combinedAddendum.slice(0, 200);
    if (basedOnUrl) input.basedOnUrl = basedOnUrl;
    reportDraft(null);
    generation.start(input);
  };

  const saveSelectedVariant = async () => {
    if (!league?._id || !selectedVariantUrl) return;
    setSaving(true);
    try {
      const token = await getToken();
      await selectLogoVariant(league._id, selectedVariantUrl, token);
      reportDraft(null);
      // updatedAt bumped locally so the ?v= cache-buster shows the NEW logo
      // in the current-logo thumbnail immediately.
      onSaved?.({ ...league, hasLogo: true, logoExt: 'png', updatedAt: new Date().toISOString() });
    } catch (err: any) {
      generation.fail(err?.response?.data?.error || err?.message || 'Failed to save logo');
    } finally {
      setSaving(false);
    }
  };

  const busy = generation.jobState === 'waiting' || generation.jobState === 'active';
  const savedLogoUrl = league?.hasLogo
    ? `${CDN}/leagues/${String(league.name || '').toLowerCase()}/leaguelogo.${league.logoExt}?v=${Date.parse(league.updatedAt) || 0}`
    : null;

  return (
    <div className="space-y-4">
      <p className="text-xs text-muted-foreground">
        One logo serves your whole site. It appears on light and dark pages, so
        transparent PNGs with mid-tone colors work best.
      </p>

      {/* Mode toggle */}
      <div className="flex gap-2" role="tablist" aria-label="Logo source">
        {([
          // Generate-first order matches the generate-first default (owner 2026-09-25).
          { key: 'generate', label: 'Generate with AI', icon: Sparkles },
          { key: 'upload', label: 'Upload', icon: Upload },
        ] as const).map(({ key, label, icon: Icon }) => (
          <button
            key={key}
            type="button"
            role="tab"
            aria-selected={mode === key}
            onClick={() => setMode(key)}
            className={`flex items-center gap-1.5 rounded-xl px-4 py-2 text-sm font-medium transition-colors ${
              mode === key ? 'bg-foreground text-white shadow-sm' : 'bg-muted/50 text-foreground hover:bg-muted'
            }`}
          >
            <Icon className="h-3.5 w-3.5" />
            {label}
          </button>
        ))}
      </div>

      {savedLogoUrl && (
        <div className="flex items-center gap-3">
          {/* Checkered wrap so transparent logos read against any ground. */}
          <span
            className="inline-flex h-14 w-14 items-center justify-center overflow-hidden rounded-lg ring-1 ring-border"
            style={{ background: 'repeating-conic-gradient(#e5e7eb 0% 25%, #ffffff 0% 50%) 0 0 / 16px 16px' }}
          >
            <img src={savedLogoUrl} alt="Current logo" className="max-h-12 max-w-12 object-contain" />
          </span>
          <span className="text-xs text-muted-foreground">
            Current logo
            <span className="block text-muted-foreground/70">
              To change it, upload a new file or generate new variants. Saving replaces it everywhere.
            </span>
          </span>
        </div>
      )}

      {mode === 'upload' && (
        <div className="flex items-center gap-3">
          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) uploadFile(f);
              e.target.value = '';
            }}
          />
          <Button variant="outline" onClick={() => fileRef.current?.click()} disabled={uploading}>
            <Upload className="mr-2 h-4 w-4" />
            {uploading ? 'Uploading…' : league?.hasLogo ? 'Replace logo' : 'Upload logo'}
          </Button>
        </div>
      )}

      {mode === 'generate' && (
        <div className="space-y-4">
          {/* Vibe — visual tiles like Style (owner 2026-09-25): the vibe name
              dressed the way that vibe's logo would feel. */}
          <div>
            <div className="mb-2 flex items-center justify-between">
              <span className="text-sm font-medium text-foreground">Vibe</span>
              <span className="text-xs text-muted-foreground">{SPORT_NAMES[leagueSport] || leagueSport}</span>
            </div>
            <div className="grid grid-cols-3 gap-2" role="radiogroup" aria-label="Logo vibe">
              {(['pro', 'amateur', 'social'] as const).map((vibe) => {
                const pv = VIBE_PREVIEWS[vibe];
                const selected = selectedVibe === vibe;
                return (
                  <button
                    key={`vibe-${vibe}`}
                    type="button"
                    role="radio"
                    aria-checked={selected}
                    title={pv.caption}
                    onClick={() => setSelectedVibe(vibe)}
                    data-testid={`logo-vibe-${vibe}`}
                    className={`overflow-hidden rounded-xl border text-left transition-colors cursor-pointer ${
                      selected ? 'border-primary ring-2 ring-primary' : 'border-border hover:border-muted-foreground/40'
                    }`}
                  >
                    <span className="flex h-12 items-center justify-center px-1" style={pv.container}>
                      <span className="whitespace-nowrap" style={pv.text}>{pv.name}</span>
                    </span>
                    <span className="block truncate px-2 py-1 text-[11px] text-muted-foreground">{pv.caption}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Style — visual tiles (owner 2026-09-25): each tile renders the
              style NAME as a mock logo in that style, so picking one is
              picking the look, not reading a label. CSS-only previews —
              STYLE_PREVIEWS below; unknown keys fall back to a plain tile. */}
          <div>
            <span className="mb-2 block text-sm font-medium text-foreground">Style</span>
            <div className="grid grid-cols-2 gap-2" role="radiogroup" aria-label="Logo style">
              {getBannerStyleList().map((s) => {
                const pv = STYLE_PREVIEWS[s.key] || STYLE_PREVIEWS.minimal;
                const selected = selectedStyle === s.key;
                return (
                  <button
                    key={`style-${s.key}`}
                    type="button"
                    role="radio"
                    aria-checked={selected}
                    title={s.description}
                    onClick={() => setSelectedStyle(s.key)}
                    data-testid={`logo-style-${s.key}`}
                    className={`overflow-hidden rounded-xl border text-left transition-colors cursor-pointer ${
                      selected ? 'border-primary ring-2 ring-primary' : 'border-border hover:border-muted-foreground/40'
                    }`}
                  >
                    <span
                      className="flex h-14 items-center justify-center px-2"
                      style={pv.container}
                    >
                      <span className="whitespace-nowrap" style={pv.text}>{s.name}</span>
                    </span>
                    <span className="block truncate px-2 py-1 text-[11px] text-muted-foreground">{s.description}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Colors */}
          <div>
            <div className="mb-2 flex items-center justify-between">
              <span className="text-sm font-medium text-foreground">Colors</span>
              <label className="flex items-center gap-2 text-xs text-muted-foreground">
                <input
                  type="checkbox"
                  checked={useCustomColors}
                  onChange={(e) => setUseCustomColors(e.target.checked)}
                  className="h-4 w-4 rounded border-input"
                />
                Use custom hex colors
              </label>
            </div>
            {useCustomColors ? (
              <div className="flex gap-2">
                {([
                  { value: customPrimary, set: setCustomPrimary, label: 'Primary' },
                  { value: customSecondary, set: setCustomSecondary, label: 'Secondary' },
                ] as const).map(({ value, set, label }) => (
                  <label key={label} className="block flex-1">
                    <span className="mb-1 block text-xs text-muted-foreground">{label}</span>
                    <input
                      type="text"
                      inputMode="text"
                      value={value}
                      onChange={(e) => set(e.target.value.toLowerCase().replace(/[^#0-9a-f]/g, '').slice(0, 7))}
                      placeholder="#2563eb"
                      className="w-full rounded-xl border-0 bg-muted/50 px-3 py-2 text-sm text-foreground ring-1 ring-border focus:bg-background focus:outline-none focus:ring-2 focus:ring-ring"
                    />
                  </label>
                ))}
              </div>
            ) : (
              <div className="flex flex-wrap gap-2">
                {paletteList.map((p) => (
                  <button
                    key={`logo-pal-${p.key}`}
                    type="button"
                    onClick={() => setPaletteKey(p.key)}
                    aria-pressed={activePalette.key === p.key}
                    className={`flex items-center gap-1.5 rounded-xl px-2.5 py-2 transition-all ${
                      activePalette.key === p.key ? 'ring-2 ring-foreground' : 'ring-1 ring-border hover:ring-input'
                    }`}
                    title={p.key.replace(/_/g, ' ')}
                  >
                    <span className="h-5 w-5 rounded-md" style={{ backgroundColor: p.primary }} />
                    <span className="h-5 w-5 rounded-md" style={{ backgroundColor: p.secondary }} />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Extra direction */}
          <label className="block">
            <span className="mb-1 block text-sm font-medium text-foreground">Extra direction (optional)</span>
            <input
              type="text"
              value={promptAddendum}
              onChange={(e) => setPromptAddendum(e.target.value.slice(0, 200))}
              placeholder={`e.g. ${LOGO_MOTIF_HINTS[leagueSport] || LOGO_MOTIF_HINTS.default}`}
              className="w-full rounded-xl border-0 bg-muted/50 px-3 py-2 text-sm text-foreground ring-1 ring-border placeholder:text-muted-foreground/60 focus:bg-background focus:outline-none focus:ring-2 focus:ring-ring"
            />
          </label>

          {/* Generate + status. The "Logo variants" label only exists once
              there ARE variants (owner 2026-09-25) — before that the row is
              just the Generate button. */}
          <div className={`flex items-center gap-3 ${generation.variants.length > 0 ? 'justify-between' : 'justify-end'}`}>
            {generation.variants.length > 0 && (
              <span className="text-sm font-medium text-foreground">Logo variants</span>
            )}
            <button
              type="button"
              onClick={() => startGeneration(null)}
              disabled={busy}
              className="flex items-center gap-2 rounded-xl bg-gradient-to-r from-primary to-primary-deep px-4 py-2 text-sm font-semibold text-white shadow-md transition-all hover:shadow-lg active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-60"
            >
              {busy ? (
                <>
                  <svg className="h-4 w-4 animate-spin" viewBox="0 0 24 24" fill="none">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  Generating{generation.stage ? `… ${generation.stage}` : '…'}
                </>
              ) : generation.variants.length > 0 ? 'Regenerate' : 'Generate logos'}
            </button>
          </div>

          {generation.error && (
            <div className="rounded-xl bg-destructive/10 px-4 py-3 text-sm text-destructive ring-1 ring-destructive/30">
              {generation.error}
            </div>
          )}

          {generation.variants.length === 0 && generation.jobState === 'idle' && (
            <p className="text-sm text-muted-foreground">
              Pick a vibe and colors, then generate 3 logo variants to choose from. Generation takes ~10–30 seconds.
            </p>
          )}

          <VariantGrid
            variants={generation.variants}
            selectedUrl={selectedVariantUrl}
            onSelect={reportDraft}
            onMoreLikeThis={(variantUrl, overrides) => startGeneration(variantUrl, overrides)}
            busy={busy}
            narrow={narrow}
            styleOptions={getBannerStyleList()}
            defaultStyleKey={selectedStyle}
            imageWrapClassName="overflow-hidden rounded-xl aspect-square [background:repeating-conic-gradient(#e5e7eb_0%_25%,#ffffff_0%_50%)_0_0_/_16px_16px]"
          />

          {selectedVariantUrl && (
            <Button onClick={saveSelectedVariant} disabled={saving}>
              {saving ? 'Saving…' : 'Save logo'}
            </Button>
          )}
        </div>
      )}
    </div>
  );
}
